import { useMutation, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import {
  IAmount,
  IFeeInSale,
  IInvoiceCustomer,
  IProduct,
  IProductForSale,
  IProductQuanityRowItem,
  IStockRequestItem,
  IUser,
  STOCKREQUEST_STATUS,
} from "../types/types";
import { EXHUT_DELIVERY_LOCAL_STORAGE_TOKEN } from "../utils/constants";
import { getAuthUrl, getBackendUrl } from "../helpers/env.helper";

export function useCreateDeliveryProfile(orgId?: string, tableId?: string) {
  return _usePost<any, any, any>({
    path: `batch-delivery/profile`,
    keys: ["profile"],
    resetKeys: [],
    replacePathParams: "tableId",
  });
}

export function useAcceptBatchTrip() {
  return _usePost<any, any, { batchId: string }>({
    path: `batch-delivery/batches/:batchId/accept`,
    keys: ["batches", "accept"],
    resetKeys: [],
    replacePathParams: "batchId",
  });
}

export function useCompleteDelivery(batchId?: string, deliveryId?: string) {
  return _usePost<any, any, { deliveryId: string }>({
    path: `batch-delivery/batches/${batchId}/deliveries/:deliveryId/complete`,
    keys: ["batches", batchId!, "deliveries", deliveryId!],
    resetKeys: [],
    replacePathParams: "deliveryId",
  });
}

export function useRotateRestaurantTableauthCode(
  orgId?: string,
  tableId?: string,
) {
  return _usePost<any, any, any>({
    path: `organizations/${orgId}/restaurant-tables/:tableId/rotate-code`,
    keys: ["organizations", orgId!, "restaurant-tables", tableId!],
    replacePathParams: "tableId",
  });
}

export interface ICreateRestaurantTablePayload {
  name: string;
  floor?: string;
  zone?: string;
  capacity?: number;
  available?: boolean;
}
export function useCreateRestaurantTable(orgId?: string) {
  return _usePost<any, any, ICreateRestaurantTablePayload>({
    path: `organizations/${orgId}/restaurant-tables`,
    keys: ["organizations", orgId!, "restaurant-tables"],
  });
}

export interface ICreateFeesPayload {
  name: string;
  type: "PERCENT" | "FLAT";
  value: number;
  isIncluded?: boolean;
}
export function useCreateFees(orgId?: string) {
  return _usePost<any, any, ICreateFeesPayload>({
    path: `organizations/${orgId}/fees`,
    keys: ["organizations", orgId!, "fees"],
  });
}

export interface ICreateKioskPayload {
  firstName: string;
  lastName: string;
  password: string;
  tableId?: number;
}
export function useCreateKiosk(orgId?: string) {
  return _usePost<any, any, ICreateKioskPayload>({
    path: `organizations/${orgId}/kiosks`,
    keys: ["organizations", orgId!, "kiosks"],
  });
}

export interface ICreateStationPayload {
  name: string;
  printerId: string;
}
export function useCreateStation(orgId?: string) {
  return _usePost<any, any, ICreateStationPayload>({
    path: `organizations/${orgId}/stations`,
    keys: ["organizations", orgId!, "stations"],
  });
}

export interface ICreateStationProductPayload {
  productIds: string[];
}
export function useCreateStationProduct(orgId?: string, stationId?: string) {
  return _usePost<any, any, ICreateStationProductPayload>({
    path: `organizations/${orgId}/stations/${stationId}/products`,
    keys: ["organizations", orgId!, "stations", stationId!, "products"],
  });
}

export interface ICreatePrinterPayload {
  name: string;
  hostname: string;
  apiKey: string;
}
export function useCreatePrinter(orgId?: string) {
  return _usePost<any, any, ICreatePrinterPayload>({
    path: `organizations/${orgId}/printers`,
    keys: ["organizations", orgId!, "printers"],
  });
}

export interface ICreateReservationPayload {
  startDate: string;
  startTime: string;
  tz: string;
  serviceId?: number;
  customerName: string;
  customerPhone: string;
  empId?: number;
}
export function useCreateReservation(storeId?: string) {
  return _usePost<any, any, ICreateReservationPayload>({
    path: `public/stores/${storeId}/reservations`,
    keys: ["stores", storeId!!, "reservations"],
  });
}

export interface ICreateStoreSolutionPayload {
  name: string;
  description: string;
  durationInMins: number;
  price: number;
}
export function useCreateStoreSolution(orgId?: string) {
  return _usePost<any, any, ICreateStoreSolutionPayload>({
    path: `organizations/${orgId}/store-solutions`,
    keys: ["organizations", orgId!!, "store-solutions"],
    resetKeys: ["organizations", orgId!!, "store-solutions"],
  });
}

export interface IKitchenItemAssigneePayload {
  assignee: string;
  itemId: string;
}

export function usePostKitchenItemSetAssignee(orgId?: string) {
  return _usePost<any, any, IKitchenItemAssigneePayload>({
    path: `organizations/${orgId}/kitchen/items/:itemId/assign`,
    keys: ["organizations", orgId!!, "kitchen", "items"],
    resetKeys: ["organizations", orgId!!, "kitchen", "items"],
    replacePathParams: "itemId",
  });
}

export function usePostKitchenItemMarkProgress(orgId?: string) {
  return _usePost<any, any, any>({
    path: `organizations/${orgId}/kitchen/items/:itemId/progress`,
    keys: ["organizations", orgId!!, "kitchen", "items"],
    resetKeys: ["organizations", orgId!!, "kitchen", "items"],
    replacePathParams: "itemId",
  });
}

export function usePostKitchenItemMarkReady(orgId?: string) {
  return _usePost<any, any, any>({
    path: `organizations/${orgId}/kitchen/items/:itemId/ready`,
    keys: ["organizations", orgId!!, "kitchen", "items"],
    resetKeys: ["organizations", orgId!!, "kitchen", "items"],
    replacePathParams: "itemId",
  });
}

export function useCancelSale(orgId?: string, deviceId?: string | null) {
  return _usePost<any, any, any>({
    path: `organizations/${orgId}/sales-terminals/${deviceId}/cancel`,
    keys: ["organizations", orgId!!, "sales-terminals", deviceId!!],
    resetKeys: ["organizations", orgId!!, "sales-terminals", deviceId!!],
  });
}

export interface ICreateCRMRunPayload {
  nonce: string;
}

export function useExecuteCRM(orgId?: string, crmId?: string) {
  return _usePost<any, any, ICreateCRMRunPayload>({
    path: `organizations/${orgId}/crm/${crmId}/runs`,
    keys: ["organizations", orgId!!, "crm", crmId!!, "runs"],
    resetKeys: ["organizations", orgId!!, "crm", crmId!!, "runs"],
  });
}

export interface ICreateCRMPayload {
  name: string;
  smsMsg: string;
  customerPhoneNumbers?: string[];
  allCustomers: boolean;
  notes?: string;
}

export function useCreateCRM(orgId?: string) {
  return _usePost<any, any, ICreateCRMPayload>({
    path: `organizations/${orgId}/crm`,
    keys: ["organizations", orgId!!, "crm"],
    resetKeys: ["organizations", orgId!!, "crm"],
  });
}

interface ICreateEmployeePIIPayload {
  pan: string;
}

export function useCreateEmployeePII(orgId?: string, empId?: string) {
  return _usePost<any, any, ICreateEmployeePIIPayload>({
    path: `organizations/${orgId}/employees/${empId}/pii`,
    keys: ["organizations", orgId!!, "employees", empId!!, "pii"],
    resetKeys: ["organizations", orgId!!, "employees", empId!!, "pii"],
  });
}

export function useUnRegisterAsVendor(id?: string) {
  return _usePost<any, any, any>({
    path: `organizations/${id}/supplychain/unregister`,
    keys: ["organizations" + id!!],
  });
}

export function usePauseVendorRegistration(id?: string) {
  return _usePost<any, any, any>({
    path: `organizations/${id}/supplychain/pause`,
    keys: ["organizations" + id!!],
  });
}

export function useResumeVendorRegistration(id?: string) {
  return _usePost<any, any, any>({
    path: `organizations/${id}/supplychain/resume`,
    keys: ["organizations" + id!!],
  });
}

interface IAddProductsToVendorPayload {
  productIds: string[];
}

export function useAddProductsToVendor(id?: string, vendorId?: string) {
  return _usePost<any, any, IAddProductsToVendorPayload>({
    path: `organizations/${id}/vendors/${vendorId}/add-products`,
    keys: ["organizations" + id!!, "vendors", vendorId!!, "add-products"],
    resetKeys: [],
  });
}

export function useRegisterAsVendor(id?: string) {
  return _usePost<any, any, any>({
    path: `organizations/${id}/supplychain/register`,
    keys: ["organizations" + id!!],
    resetKeys: [],
  });
}

interface ICreateVendorPayload {
  name?: string;
  email?: string;
  phone?: string;
  vendorId?: string;
}

export function useCreateVendor(orgId?: string) {
  return _usePost<any, any, ICreateVendorPayload>({
    path: `organizations/${orgId}/vendors`,
    keys: ["organizations", orgId!!, "vendors"],
    resetKeys: ["organizations", orgId!!, "vendors"],
  });
}

export interface ICreateSubscriptionPayload {
  entitlement: number;
}

export interface ICreateSubscriptionResponse {
  approvalUrl: string;
}

export function useCreateSubscription() {
  return _usePost<ICreateSubscriptionResponse, any, ICreateSubscriptionPayload>(
    {
      path: `subscriptions`,
      keys: ["subscriptions"],
      resetKeys: ["subscriptions"],
    },
  );
}

export interface ICreateStockRequestPayload {
  vendorId: string;
  dueDate?: string;
  items: IStockRequestItem[];
  status: STOCKREQUEST_STATUS.SAVED;
}

export function useSupplyChainCreateStockRequest(orgId?: string) {
  return _usePost<any, any, ICreateStockRequestPayload>({
    path: `organizations/${orgId}/supplychain/stock-requests`,
    keys: ["supplychain", "stock-requests"],
    resetKeys: [],
  });
}

export interface ICreateOrgPayload {
  name: string;
  website: string;
  street1: string;
  street2: string;
  city: string;
  state: string;
  country: string;
  zipCode: number;
  phone: string;
}

export function useCreateOrganization() {
  return _usePost<any, any, ICreateOrgPayload>({
    path: `organizations`,
    keys: ["organizations"],
    resetKeys: [],
  });
}

export interface ISendInvoicePayload {
  pdfContent?: string;
  customMsg?: string;
  fileName?: string;
  mode: "SMS" | "EMAIL";
  emailFormat?: "PDF" | "INLINE";
}

export function useSendInvoice(orgId?: string, iid?: string) {
  return _usePost<any, any, ISendInvoicePayload>({
    path: `organizations/${orgId}/invoices/${iid}/send`,
    keys: ["organizations", orgId!!, "invoices", iid!!],
    resetKeys: [],
  });
}

export function useSendInvoiceReceipt(orgId?: string, iid?: string) {
  return _usePost<any, any, ISendInvoicePayload>({
    path: `organizations/${orgId}/invoices/${iid}/receipts/send`,
    keys: ["organizations", orgId!!, "invoices", iid!!, "receipts"],
    resetKeys: [],
  });
}

export interface ICreateInvoicePayload {
  products?: string[];
  total: number;
  grossTotal: number;
  tax: number;
  items?: IProductQuanityRowItem[];
  customer?: IInvoiceCustomer;
  notes?: string;
  internalNotes?: string;
  includeFooter?: boolean;
  includePaymentLink?: boolean;
}

export function useCreateInvoice(orgId?: string) {
  return _usePost<any, any, ICreateInvoicePayload>({
    path: `organizations/${orgId}/invoices`,
    keys: ["organizations", orgId!!, "invoices"],
    resetKeys: [],
  });
}
export interface ICreateOrgInvestmentPayload {
  category: string;
  amount: number;
  investmentDate: string;
  notes?: string;
}

export function useCreateOrgInvestment(orgId?: string) {
  return _usePost<any, any, ICreateOrgInvestmentPayload>({
    path: `organizations/${orgId}/investments`,
    keys: ["organizations", orgId!!, "investments"],
    resetKeys: ["organizations", orgId!!, "investments"],
  });
}

export interface ICreatePayloadSchedulePayload {
  startDate: string;
  schedule: string;
}

export function useCreatePayrollSchedule(orgId?: string) {
  return _usePost<any, any, ICreatePayloadSchedulePayload>({
    path: `organizations/${orgId}/payroll-schedules`,
    keys: ["organizations", orgId!!, "payroll-schedules"],
    resetKeys: ["organizations", orgId!!, "payroll-schedules"],
  });
}

export interface ICreatePayrollCyclePayload {
  type: string;
  startDate?: string;
  endDate?: string;
}

export function useCreatePayrollCycle(orgId?: string) {
  return _usePost<any, any, ICreatePayrollCyclePayload>({
    path: `organizations/${orgId}/payroll-cycles`,
    keys: ["organizations", orgId!!, "payroll-cycles"],
    resetKeys: ["organizations", orgId!!, "payroll-cycles"],
  });
}

interface ICreatePayrollPayload {
  empId?: number;
  cycleId: number;
}

export function useCreatePayroll(orgId?: string) {
  return _usePost<any, any, ICreatePayrollPayload>({
    path: `organizations/${orgId}/payroll`,
    keys: ["organizations", orgId!!, "payroll"],
    resetKeys: ["organizations", orgId!!, "payroll"],
  });
}

export function useCreatePayrollForEmployee(orgId?: string, cycleId?: string) {
  return _usePost<any, any, ICreatePayrollPayload>({
    path: `organizations/${orgId}/payroll`,
    keys: ["organizations", orgId!!, "payroll", cycleId!!, "employee"],
    resetKeys: [
      "organizations",
      orgId!!,
      "payroll-cycles",
      cycleId!!,
      "payroll",
    ],
    replacePathParams: "empId",
  });
}

export interface ICreatePayrollBonusPayload {
  empId: number;
  type: string;
  bonusType: string;
  amount: number;
}

export function useCreatePayrollBonus(orgId?: string, cycleId?: string) {
  return _usePost<any, any, ICreatePayrollBonusPayload>({
    path: `organizations/${orgId}/payroll-cycles/${cycleId}/bonuses`,
    keys: ["organizations", orgId!!, "payroll-cycles", cycleId!!, "bonuses"],
    resetKeys: [
      "organizations",
      orgId!!,
      "payroll-cycles",
      cycleId!!,
      "bonuses",
    ],
  });
}

export interface ICreatePaymentPayload {
  invoiceId?: number;
  paymentType?: string;
  paymentDate: string;
  amount: number;
  customerPhone?: string;
  notes?: string;
  currency: string;
  nonce: string;
}

export function useCreatePayment(orgId?: string) {
  return _usePost<any, any, ICreatePaymentPayload>({
    path: `organizations/${orgId}/payments`,
    keys: ["organizations", orgId!!, "payments"],
    resetKeys: ["organizations", orgId!!, "payments"],
  });
}

export function useProcessPayment(orgId?: string, paymentId?: string) {
  return _usePost<any, any, any>({
    path: `organizations/${orgId}/payments/:paymentId/process`,
    keys: ["organizations", orgId!, "payments", paymentId!],
    resetKeys: ["organizations", orgId!, "payments"],
    replacePathParams: "paymentId",
  });
}

export interface ICreateOrgExpensePayload {
  category: string;
  amount: number;
  expenseDate: string;
  notes: string;
}

export function useCreateOrgExpense(orgId?: string) {
  return _usePost<any, any, ICreateOrgExpensePayload>({
    path: `organizations/${orgId}/expenses`,
    keys: ["organizations", orgId!!, "expenses"],
    resetKeys: [],
  });
}

export interface ICreateReimbursementPayload {
  category: string;
  amount: number;
  expenseDate: string;
  notes?: string;
}

export function useCreateReimbursement(orgId?: string) {
  return _usePost<any, any, ICreateReimbursementPayload>({
    path: `organizations/${orgId}/reimbursements`,
    keys: ["organizations", orgId!!, "reimbursements"],
    resetKeys: [],
  });
}

export interface ICreateReimbursementAttachmentPayload {
  reimbursementId?: string;
  attachment?: any;
}

export interface IUpdateReimbursementStatusPayload {
  status: string;
  notes?: string;
  // following will be used by employee if he wants to update the reimbursement details
  expenseDate?: string;
  category?: string;
  amount?: number;
}

export function useUpdateReimbursement(id?: string, rid?: string) {
  return _usePost<any, any, IUpdateReimbursementStatusPayload>({
    path: `organizations/${id}/reimbursements/${rid}/status`,
    keys: ["organizations", id!!, "reimbursements", rid!!],
    resetKeys: [],
  });
}

interface ITimesheetPayload {
  timeSheetDate: string;
  hours: number;
  notes: string;
  startTime?: string;
  endTime?: string;
}

export function useCreateTimesheet(orgId?: string) {
  return _usePost<any, any, ITimesheetPayload>({
    path: `organizations/${orgId}/timesheets`,
    keys: ["organizations", orgId!!, "timesheets"],
    resetKeys: [],
  });
}

export interface IRewardLevelPayload {
  name: string;
  type: string;
  value: number;
  monetaryType: string;
  minAmount: number;
}

export function useCreateRewardLevel(orgId?: string) {
  return _usePost<any, any, IRewardLevelPayload>({
    path: `organizations/${orgId}/rewards-level`,
    keys: ["organizations", orgId!!, "rewards-level"],
    resetKeys: [],
  });
}

interface IRewardTransactionPayload {
  phone: string;
  billAmount?: number;
  levelId?: number;
}

export function useCreateReward(orgId?: string) {
  return _usePost<any, any, IRewardTransactionPayload>({
    path: `organizations/${orgId}/rewards`,
    keys: ["organizations", orgId!!, "rewards"],
    resetKeys: [],
  });
}

interface IRedemptionPayload {
  phone: string;
  rewards: number;
}

export function useCreateRedemption(orgId?: string) {
  return _usePost<any, any, IRedemptionPayload>({
    path: `organizations/${orgId}/redemption`,
    keys: ["organizations", orgId!!, "redemption"],
    resetKeys: [],
  });
}

interface IPrintSalePayload {
  printerId: string;
}

export function usePostPrintSale(orgId?: string, orderId?: string) {
  return _usePost<any, any, IPrintSalePayload>({
    path: `organizations/${orgId}/sales/${orderId}/print`,
    keys: ["organizations", orgId!!, "sales", orderId!!, "print"],
    resetKeys: [],
  });
}

export interface ICreateSalePayload {
  category: string;
  amount: number;
  saleDate: string;
  notes?: string;
  paymentType?: string;
  phone?: string;
  products?: IProductForSale[];
  tax: number;
  discountTotal?: number;
  pointsTotal?: number;
  coupons?: number[];
  type: "RESTAURANT" | "FASTFOOD" | "RETAIL" | "QUICK";
  total: number;
  tip?: number;
  paymentNonce: string;
  terminalId?: string | null;
  storeCreditsTotal?: number | null;
  fees?: IFeeInSale[];
  printerId?: string | null;
}

export function useCreateOrgSale(orgId?: string) {
  return _usePost<any, any, ICreateSalePayload>({
    path: `organizations/${orgId}/sales`,
    keys: ["organizations", orgId!!, "sales"],
    resetKeys: [],
  });
}

export interface IProcessReturnPayload {
  itemsTotal: number;
  grossTotal: number;
  discountTotal: number;
  notes?: string;
  refundPaymentType?: string;
  products: IProductForSale[];
  phone: string;
  terminalId?: string | null;
  paymentNonce?: string;
  printerId?: string | null;
}

export function useProcessReturn(orgId?: string, saleId?: string) {
  return _usePost<any, any, IProcessReturnPayload>({
    path: `organizations/${orgId}/sales/${saleId}/returns`,
    keys: ["organizations", orgId!!, "sales", saleId!!, "returns"],
    resetKeys: [],
  });
}

export interface IAddTablePayload {
  value: string;
  kioskEmpId?: string;
}

export interface IAddTableResponse {
  value: string;
  id: string;
  orgId: string;
  notes: string;
}

export function useAddRestaurantTable(orgId?: string) {
  return _usePost<IAddTableResponse, any, IAddTablePayload>({
    path: `organizations/${orgId}/restaurant-tabs`,
    keys: ["organizations", orgId!!, "restaurant-tabs"],
    resetKeys: [],
  });
}

export interface IAddItemsToRestaurantTablePayload {
  products: IProductForSale[];
}

export function useAddItemsToRestaurantTable(orgId?: string, tableId?: string) {
  return _usePost<IAddTableResponse, any, IAddItemsToRestaurantTablePayload>({
    path: `organizations/${orgId}/restaurant-tabs/${tableId}/add-items`,
    keys: ["organizations", orgId!!, "restaurant-tabs", tableId!],
    resetKeys: [],
  });
}

export interface ICancelItemsToRestaurantTablePayload {
  productUniqueId: string;
}

export function useCancelItemsToRestaurantTable(
  orgId?: string,
  tableId?: string,
) {
  return _usePost<IAddTableResponse, any, ICancelItemsToRestaurantTablePayload>(
    {
      path: `organizations/${orgId}/restaurant-tabs/${tableId}/cancel-items`,
      keys: ["organizations", orgId!!, "restaurant-tabs", tableId!],
      resetKeys: [],
    },
  );
}

export function useCallWaiter(orgId?: string, tableId?: string) {
  return _usePost<any, any, any>({
    path: `organizations/${orgId}/restaurant-tabs/${tableId}/call-waiter`,
    keys: ["organizations", orgId!!, "restaurant-tabs", tableId!],
    resetKeys: [],
  });
}

export function useResetWaiter(orgId?: string, tableId?: string) {
  return _usePost<any, any, any>({
    path: `organizations/${orgId}/restaurant-tabs/${tableId}/reset-waiter`,
    keys: ["organizations", orgId!!, "restaurant-tabs", tableId!],
    resetKeys: [],
  });
}

export function useRestaurantTabCheckoutReady(
  orgId?: string,
  tableId?: string,
) {
  return _usePost<any, any, any>({
    path: `organizations/${orgId}/restaurant-tabs/${tableId}/checkout-ready`,
    keys: ["organizations", orgId!!, "restaurant-tabs", tableId!],
    resetKeys: [],
  });
}

export function useRestaurantTabCancelCheckoutReady(
  orgId?: string,
  tableId?: string,
) {
  return _usePost<any, any, any>({
    path: `organizations/${orgId}/restaurant-tabs/${tableId}/cancel-checkout-ready`,
    keys: ["organizations", orgId!!, "restaurant-tabs", tableId!],
    resetKeys: [],
  });
}

interface IAddEmployeePayload {
  email: string;
  startDate?: string;
  employmentType: string;
  salary: number;
}

export function useAddEmployeeToOrg(orgId?: string) {
  return _usePost<any, any, IAddEmployeePayload>({
    path: `organizations/${orgId}/employees`,
    keys: ["organizations", orgId!!, "employees"],
    resetKeys: [],
  });
}

interface IAddDeductionPayload {
  name: string;
  type: string;
  value: number;
}

export function useAddDeductionToEmployee(orgId?: string, empId?: string) {
  return _usePost<any, any, IAddDeductionPayload>({
    path: `organizations/${orgId}/employees/${empId}/deductions`,
    keys: ["organizations", orgId!!, "employees", empId!!, "deductions"],
    resetKeys: [],
  });
}

export interface IAuthenticatePayload {
  email: string;
  password: string;
}

export type IRegisterPayload = IAuthenticatePayload;

export type IAuthenticatResponse = IUser;

export function useRegisterMutation() {
  return _usePostAuthBE<void, undefined, IRegisterPayload>(
    "register?source=delivery",
    ["register"],
    [""],
    false,
  );
}

export interface IRegisterSSOPayload {
  token: string;
}

export function useRegisterSSOMutation() {
  return _usePostAuthBE<IAuthenticatResponse, undefined, IRegisterSSOPayload>(
    "register/sso?source=business",
    [],
    [],
    false,
  );
}

export function useAuthenticate() {
  return _usePostAuthBE<IAuthenticatResponse, undefined, IAuthenticatePayload>(
    "auth",
    [],
    [],
    false,
  );
}

export interface IRegisterVerifyPayload {
  cid: string;
  regkey: string;
}

export function useRegisterVerify() {
  return _usePostAuthBE<
    IAuthenticatResponse,
    undefined,
    IRegisterVerifyPayload
  >("register/verify", [], [], false);
}

export interface IAuthenticateSSOPayload {
  token: string;
  type: "google";
}

export function useAuthenticateWithSSO() {
  return _usePostAuthBE<IAuthenticatResponse, null, IAuthenticateSSOPayload>(
    `auth/sso`,
    [],
    [],
  );
}

export function usePostLogout() {
  return _usePostAuthBE<void, null, void>("logout", [], []);
}

function _usePostAuthBE<T1, T2, T3>(
  path: string,
  keys: string[],
  resetKeys: string[],
  authenticated = true,
) {
  return _usePost<T1, T2, T3>({
    path,
    keys,
    resetKeys,
    authenticated,
    host: getAuthUrl(),
  });
}

export interface ICreateProductCategoryPayload {
  name: string;
  imageData?: string;
  imageName?: string;
  imageType?: string;
}

export function useCreateProductCategory(orgId?: string) {
  return _usePost<any, null, ICreateProductCategoryPayload>({
    path: `organizations/${orgId}/products-categories`,
    keys: ["products-categories"],
    authenticated: true,
    host: process.env.REACT_APP_PRODUCTS_URL,
  });
}

export interface ICreateProductPayload {
  title: string;
  description?: string;
  price: IAmount;
  externalId?: string;
  sku?: string;
  salesTaxExcluded?: boolean;
  stoCategories?: string[];
  isStoFeatured?: boolean;
  pricingUnit?: string;
  availableQuantity?: number;
}

export function useCreateProduct(orgId?: string) {
  return _usePost<IProduct, null, ICreateProductPayload>({
    path: `organizations/${orgId}/products`,
    keys: ["products"],
    resetKeys: ["products"],
    authenticated: true,
    host: process.env.REACT_APP_PRODUCTS_URL,
  });
}

export function useCreateProductMultiple(orgId?: string) {
  return _usePost<IProduct, null, any>({
    path: `organizations/${orgId}/products/multiple`,
    keys: ["products"],
    resetKeys: ["products"],
    authenticated: true,
    host: process.env.REACT_APP_PRODUCTS_URL,
  });
}

export interface ICreateProductImagePayload {
  image: string;
  name: string;
  type?: string;
}

export function useCreateProductImage(orgId?: string, productId?: string) {
  return _usePost<null, null, ICreateProductImagePayload>({
    path: `organizations/${orgId}/products/${productId}/images`,
    keys: ["products", productId!!, "images"],
    resetKeys: ["products", productId!!, "images"],
    authenticated: true,
    host: process.env.REACT_APP_PRODUCTS_URL,
  });
}

export interface ICreateProductImageLazyPayload {
  image: string;
  name: string;
  type?: string;
  productId: string;
}

// same as useCreateProductImage but without productId param
export function useCreateProductImageLazy(orgId?: string) {
  return _usePost<null, null, ICreateProductImageLazyPayload>({
    path: `organizations/${orgId}/products/:productId/images`,
    keys: ["products"],
    resetKeys: ["products"],
    authenticated: true,
    host: process.env.REACT_APP_PRODUCTS_URL,
    replacePathParams: "productId",
  });
}

function _usePost<T1, T2, T3>({
  path,
  keys,
  resetKeys,
  authenticated = true,
  host,
  replacePathParams,
}: {
  path: string;
  keys?: string[];
  resetKeys?: string[];
  authenticated?: boolean;
  host?: string;
  replacePathParams?: string;
}) {
  let _url = host ? `${host}/${[path]}` : `${getBackendUrl()}/${path}`;

  let headers: any = {
    "Content-Type": "application/json",
  };

  if (authenticated) {
    headers["Authorization"] = localStorage.getItem(
      EXHUT_DELIVERY_LOCAL_STORAGE_TOKEN,
    );
  } else {
    headers["Access-Type"] = "offline";
  }

  const queryClient = useQueryClient();
  return useMutation<T1, T2, T3>({
    mutationFn: async (req) => {
      if (replacePathParams) {
        _url = _url.replace(
          `:${replacePathParams}`,
          (req as any)[replacePathParams],
        );
      }
      return axios
        .post(_url, req, {
          headers,
        })
        .then((resp) => resp.data);
    },
    mutationKey: keys,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: resetKeys }),
  });
}

export interface IVerifySMSVerificationPayload {
  code: number;
  phone: string;
}

export function usePostVerifySMSVerification() {
  return _usePostAuthBE<void, any, IVerifySMSVerificationPayload>(
    `users/verifications/sms/verify`,
    ["users-verifications", "profile"],
    ["users-verifications", "profile"],
  );
}

export interface ITriggerSMSVerificationPayload {
  phone: string;
}

export function usePostTriggerSMSVerification() {
  return _usePostAuthBE<void, any, ITriggerSMSVerificationPayload>(
    `users/verifications/sms`,
    ["users-verifications", "profile"],
    ["users-verifications", "profile"],
  );
}
